import { FormBuilder, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { CmsComponent, Image, ImageType, Product } from '@spartacus/core';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { takeUntil } from 'rxjs';

import {
  CoreModalService, IComponentWrapper, IconSizeEnum, IHeading, SdBannerComponent, SdBorderWrapperComponent,
  SdComponentWrapperComponent, SdFormAutocompleteComponent, SdFormErrorComponent, SdHtmlComponent, SdIconComponent,
  SdLinkComponent, SdModalDefaultComponent, SdProductCustomerComponent, SdTitleComponent, SizesEnum, ThemesEnum
} from '@seb/web-design-system/core';

import { RepairabilityEnum, RepairabilityFlagEnum } from '../../../enums';
import { AbstractComponent } from '../abstract-component.component';
import { HeadingParagraphMapper } from '../../../mappers';
import { AutocompleteSuggestion } from '../../../models';
import { RepairabilityService } from '../../../services';
import { getMediaByFormat } from '@seb/web-common/core/utils';

@Component({
  selector: 'wc-repair-commitment-search',
  templateUrl: './repair-commitment-search.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    SdComponentWrapperComponent,
    FormsModule,
    ReactiveFormsModule,
    SdFormAutocompleteComponent,
    SdFormErrorComponent,
    SdIconComponent,
    SdBorderWrapperComponent,
    SdProductCustomerComponent,
    SdTitleComponent,
    SdLinkComponent,
    SdModalDefaultComponent,
    SdBannerComponent,
    SdHtmlComponent,
    TranslatePipe
  ],
  providers: [CoreModalService]
})
export class RepairCommitmentSearchComponent extends AbstractComponent<CmsComponent> implements OnInit {
  formGroup: FormGroup;
  autocompleteElements: AutocompleteSuggestion[] = [];
  isRefModalOpened = false;
  urlImage = '/assets/images/asset-accessory-reference.png';
  product: Product;
  productNotFound = false;
  componentWrapper: IComponentWrapper;
  themesEnum = ThemesEnum;
  sizesEnum = SizesEnum;
  iconSize = IconSizeEnum;
  repairabilityEnum = RepairabilityEnum;
  heading: IHeading;

  get productImage(): Image {
    return getMediaByFormat(this.product?.primaryImages, 'thumbnail') ?? this.product?.images?.[ImageType.PRIMARY]?.['thumbnail'];
  }

  constructor(
    private formBuilder: FormBuilder,
    private repairabilityService: RepairabilityService,
    private cdRef: ChangeDetectorRef,
    private headingParagraphMapper: HeadingParagraphMapper,
    private translateService: TranslateService
  ) {
    super();
  }

  ngOnInit(): void {
    const baseStore = this.baseStoreService.baseStore;
    const isHob = !!baseStore.houseOfBrandConfig?.brand?.length;
    this.urlImage = isHob ? '/assets/images/hob-asset-accessory-reference.png' : this.urlImage;
    // Get component even if not used because it's needed for lazy loading initializing
    this.component$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.formGroup = this.formBuilder.group({
        autocomplete: [{ value: undefined }, Validators.minLength(4)]
      });
      const richParagraphData = {
        heading: {
          headline: this.translateService.instant('repairability.search.title')
        },
        titleThemesConfig: ThemesEnum.dark,
        titleLevel: 2,
        size: SizesEnum.m
      };
      this.componentWrapper = {
        heading: this.headingParagraphMapper.toHeadingParagraph(richParagraphData)
      };
      this.setLoaded();
      this.cdRef.markForCheck();
    });
  }

  autocomplete(term: string): void {
    this.repairabilityService.autocompleteSearch(term).subscribe((suggestions) => {
      this.autocompleteElements = suggestions;
      this.cdRef.markForCheck();
    });
  }

  search(): void {
    this.productNotFound = false;
    const control = this.formGroup.get('autocomplete');
    control.markAsTouched();
    if (control.valid) {
      this.repairabilityService.searchForRepairability(control.value).subscribe((result) => {
        if (result.flag === RepairabilityFlagEnum.PRODUCT_FOUND) {
          this.product = result.product;
        } else {
          this.productNotFound = true;
          this.product = undefined;
        }
        this.cdRef.markForCheck();
      });
    }
  }

  openModalRef(): void {
    this.isRefModalOpened = true;
  }
}
